import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Bot, X, Send, Loader2, Sparkles, Trash2, ArrowRight, MessageSquare,
} from 'lucide-react';
import { copilotApi, aiActApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const STORAGE_KEY_PREFIX = 'cognis2.copilot.session.';

const SUGGESTED_PROMPTS = [
  'Quais sistemas IA tenho de alto risco?',
  'O que falta para submeter o registo EU?',
  'Quais incidentes Art. 72 estão pendentes?',
  'Que controlos faltam para o Art. 9?',
];

// Inline markdown → React (lightweight, no external dep)
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc ml-5 space-y-1 my-2">
          {listItems.map((it, i) => <li key={i}>{inlineFmt(it)}</li>)}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };
  const flushTable = () => {
    if (tableRows.length > 0) {
      const header = tableRows[0];
      const body = tableRows.slice(2); // skip separator
      elements.push(
        <div key={`tbl-${elements.length}`} className="overflow-x-auto my-2">
          <table className="text-xs border-collapse">
            <thead><tr className="bg-slate-100">{header.map((h, i) => <th key={i} className="border border-slate-200 px-2 py-1 text-left font-semibold">{inlineFmt(h)}</th>)}</tr></thead>
            <tbody>{body.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="border border-slate-200 px-2 py-1">{inlineFmt(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
    inTable = false;
  };

  for (const ln of lines) {
    if (ln.trim().startsWith('|') && ln.trim().endsWith('|')) {
      if (!inTable) { flushList(); inTable = true; }
      tableRows.push(ln.trim().slice(1, -1).split('|').map(c => c.trim()));
      continue;
    }
    flushTable();
    if (ln.trim().startsWith('- ') || ln.trim().startsWith('* ')) {
      if (!inList) { inList = true; }
      listItems.push(ln.trim().slice(2));
    } else {
      flushList();
      if (ln.trim()) elements.push(<p key={`p-${elements.length}`} className="my-1">{inlineFmt(ln)}</p>);
    }
  }
  flushList();
  flushTable();
  return elements;
}

function inlineFmt(s) {
  // Bold **text**
  const parts = [];
  let remaining = s;
  let idx = 0;
  const boldRe = /\*\*([^*]+)\*\*/g;
  let m;
  let last = 0;
  while ((m = boldRe.exec(remaining)) !== null) {
    if (m.index > last) parts.push(remaining.slice(last, m.index));
    parts.push(<strong key={`b-${idx++}`} className="font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < remaining.length) parts.push(remaining.slice(last));
  return parts;
}

export const CopilotFAB = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [systems, setSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const sid = localStorage.getItem(`${STORAGE_KEY_PREFIX}${user.user_id}`);
    if (sid) setSessionId(sid);
  }, [user]);

  useEffect(() => {
    // Lazy-load systems list only when panel opens for the first time
    if (isOpen && systems.length === 0) {
      aiActApi.listSystems({})
        .then(res => setSystems(res.data))
        .catch(() => { /* silent */ });
    }
  }, [isOpen, systems.length]);

  useEffect(() => {
    if (isOpen && sessionId && messages.length === 0) {
      copilotApi.getSession(sessionId)
        .then(res => {
          if (res.data.messages?.length) {
            setMessages(res.data.messages);
          }
        })
        .catch(() => { /* silent — session may not exist on backend yet */ });
    }
    // scroll to bottom on new message
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [isOpen, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: msg, ts: new Date().toISOString() };
    setMessages(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await copilotApi.chat({
        session_id: sessionId,
        message: msg,
        system_id: selectedSystem || null,
      });
      if (!sessionId) {
        setSessionId(res.data.session_id);
        if (user?.user_id) localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.user_id}`, res.data.session_id);
      }
      const asstMsg = {
        role: 'assistant',
        content: res.data.answer,
        references: res.data.references,
        actions: res.data.actions,
        ts: new Date().toISOString(),
      };
      setMessages(m => [...m, asstMsg]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro no Compliance Co-pilot');
      setMessages(m => [...m, { role: 'assistant', content: '⚠️ Erro ao processar pergunta. Tente reformular.', error: true }]);
    } finally { setLoading(false); }
  };

  const newSession = async () => {
    if (sessionId) {
      try { await copilotApi.deleteSession(sessionId); } catch { /* silent */ }
    }
    setSessionId(null);
    if (user?.user_id) localStorage.removeItem(`${STORAGE_KEY_PREFIX}${user.user_id}`);
    setMessages([]);
    toast.success('Nova conversa iniciada');
  };

  const handleAction = (href) => {
    setIsOpen(false);
    navigate(href);
  };

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="copilot-fab"
          className="fixed bottom-20 right-6 z-40 group flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
          aria-label="Abrir Compliance Co-pilot"
        >
          <Bot className="h-5 w-5" />
          <span className="hidden sm:inline font-medium text-sm pr-1">Compliance Co-pilot</span>
          <Sparkles className="h-3.5 w-3.5 absolute -top-1 -right-1 text-yellow-300 animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-0 right-0 z-50 w-full sm:w-[440px] sm:bottom-20 sm:right-6 h-[100vh] sm:h-[640px] sm:max-h-[80vh] bg-white border border-slate-200 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="copilot-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-9 w-9 bg-white/15 rounded-full flex items-center justify-center shrink-0">
                <Bot className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">Compliance Co-pilot</p>
                <p className="text-[10px] text-indigo-100 truncate">Motor de IA • EU AI Act + NIS2</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={newSession} className="text-white hover:bg-white/10 h-8 w-8 p-0" title="Nova conversa" data-testid="copilot-new-session">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="text-white hover:bg-white/10 h-8 w-8 p-0" data-testid="copilot-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* System context selector */}
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
            <select
              value={selectedSystem}
              onChange={e => setSelectedSystem(e.target.value)}
              className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white"
              data-testid="copilot-system-select"
            >
              <option value="">— Contexto: estado global do tenant —</option>
              {systems.map(s => <option key={s.system_id} value={s.system_id}>📋 Foco: {s.name}</option>)}
            </select>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="copilot-messages">
            {messages.length === 0 && (
              <div className="space-y-3" data-testid="copilot-empty-state">
                <div className="text-center py-4">
                  <div className="h-12 w-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="h-6 w-6 text-indigo-600" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">Como posso ajudar?</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Pergunte sobre os seus sistemas IA, prazos de compliance, ou que controlos faltam.
                  </p>
                </div>
                <p className="text-xs text-slate-400 uppercase tracking-wider px-1">Sugestões:</p>
                {SUGGESTED_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => send(p)}
                    className="w-full text-left text-sm bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-lg px-3 py-2 transition-colors"
                    data-testid={`copilot-suggestion-${i}`}
                  >
                    <MessageSquare className="h-3 w-3 inline mr-2 text-indigo-500" />
                    {p}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, idx) => (
              <div key={idx} className={cn('flex gap-2', m.role === 'user' ? 'justify-end' : 'justify-start')} data-testid={`copilot-msg-${idx}`}>
                {m.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center shrink-0 mt-1">
                    <Bot className="h-4 w-4 text-indigo-600" />
                  </div>
                )}
                <div className={cn(
                  'max-w-[85%] text-sm rounded-2xl px-3 py-2',
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : m.error
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                      : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                )}>
                  {m.role === 'assistant' ? renderMarkdown(m.content) : <p>{m.content}</p>}
                  {m.references?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-1">
                      {m.references.map((r, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] bg-white" data-testid={`copilot-ref-${idx}-${i}`}>{r}</Badge>
                      ))}
                    </div>
                  )}
                  {m.actions?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.actions.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => handleAction(a.href)}
                          className="w-full text-left text-xs bg-white border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-lg px-2 py-1.5 flex items-center justify-between transition-colors"
                          data-testid={`copilot-action-${idx}-${i}`}
                        >
                          <span className="font-medium text-indigo-700 truncate">{a.label}</span>
                          <ArrowRight className="h-3 w-3 text-indigo-600 shrink-0 ml-1" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 justify-start" data-testid="copilot-loading">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 text-sm flex items-center gap-2 text-slate-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  A pensar...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 bg-white">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex gap-2 items-end"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pergunte ao Compliance Bot..."
                disabled={loading}
                className="flex-1"
                data-testid="copilot-input"
                maxLength={500}
              />
              <Button type="submit" disabled={loading || !input.trim()} size="sm" data-testid="copilot-send-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
            <p className="text-[9px] text-slate-400 mt-1 text-center">
              Respostas geradas por IA. Reveja sempre com um especialista. Não constitui aconselhamento legal.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CopilotFAB;
